import resolveFrontendModule from "@app/resolveModule/frontend";
import Path from "@core/FileProvider/Path/Path";
import ErrorConfirmService from "@ext/errorHandlers/client/ErrorConfirmService";
import t from "@ext/localization/locale/translate";
import { pdfSaveFailed } from "@ext/print/errors/PdfSaveFailed";
import { toast } from "@ui-kit/Toast";
import { useMemo } from "react";

// Inline code outside `.article`: the same tokens as an article's inline code span.
const FileName = ({ children }: { children: string }) => (
	<code className="rounded-[var(--radius-small)] bg-[var(--color-code-bg)] p-0.5">{children}</code>
);

// Characters a file name cannot hold on Windows; macOS forbids `/` and `:` of them.
const fileNameOf = (title: string) => title.replace(/[\\/:*?"<>|]/g, "-");

/** The export writes the PDF file itself where the platform can (macOS and Windows desktop); elsewhere null — the print dialog stays. */
export const usePdfFile = (title: string) =>
	useMemo(() => {
		const saver = resolveFrontendModule("pdfFileSaver")();
		if (!saver) return null;

		return {
			chooseDestination: async (chooseAgain: () => void): Promise<string | null> => {
				const destination = await saver.chooseDestination(`${fileNameOf(title)}.pdf`);
				if (destination.kind === "notWritable")
					ErrorConfirmService.notify(pdfSaveFailed(destination.path, destination.reason, chooseAgain));
				return destination.kind === "chosen" ? destination.path : null;
			},
			save: saver.save,
			showSaved: (path: string) =>
				// Without size and focus the frame, the layout and the button each fall back to a different ui-kit
				// default: a grey button on the green frame, and a layout that drops the description.
				toast(t("export.pdf.saved.title"), {
					status: "success",
					size: "lg",
					focus: "medium",
					description: <FileName>{new Path(path).nameWithExtension}</FileName>,
					primaryAction: {
						title: t("export.pdf.saved.showInFolder"),
						onClick: () => saver.showInFolder(path),
					},
				}),
		};
	}, [title]);
